import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Product } from '@app/shared/classes/product';

@Component({
  selector: 'capture-ticket-names',
  templateUrl: './capture-ticket-names.component.html',
  styleUrls: ['./capture-ticket-names.component.scss']
})
export class CaptureTicketNamesComponent implements OnInit {
  @Input() products: Product[] = [];
  @Output() ticketData = new EventEmitter<any[]>();
  @Output() ticketsValid = new EventEmitter<boolean>();
  captureForm: FormGroup;
  
  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.captureForm = this.fb.group({
      ticketForms: this.fb.array([])
    });
    
    const validProducts = [175917, 175918, 175919, 175920, 175967];
    const ticketProducts = this.products.filter(product => validProducts.includes(product.id));
    
    if (ticketProducts.length > 0) {
      ticketProducts.forEach(product => {
        for (let i = 0; i < (product.quantity || 1); i++) {
          this.addTicketForm(product, i);
        }
      });
      
      // Set up form validation listener
      this.captureForm.valueChanges.subscribe(() => {
        this.validateAndEmit();
      });
      
      // Initial validation
      this.validateAndEmit();
    } else {
      this.ticketsValid.emit(true);
    }
  }

  private addTicketForm(product: Product, index: number): void {
    const ticketForms = this.captureForm.get('ticketForms') as FormArray;
    
    ticketForms.push(
      this.fb.group({
        productId: [product.id],
        productTitle: [product.title],
        ticketIndex: [index],
        fullName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        industryType: ['', [Validators.required, Validators.minLength(4)]]
      })
    );
  }

  get ticketForms(): FormArray {
    return this.captureForm.get('ticketForms') as FormArray;
  }

  getTicketFormGroup(index: number): FormGroup {
    return this.ticketForms.at(index) as FormGroup;
  }

  getTicketData(): any[] {
    if (this.captureForm.valid) {
      return this.ticketForms.value;
    }
    return [];
  }

  isFormValid(): boolean {
    // If there are no ticket forms, consider it valid
    if (this.ticketForms.length === 0) {
      return true;
    }
    return this.captureForm.valid;
  }
  
  validateAndEmit(): void {
    const isValid = this.isFormValid();
    this.ticketsValid.emit(isValid);
    
    if (isValid) {
      this.ticketData.emit(this.getTicketData());
    } else {
      this.ticketData.emit([]);
    }
    
    // Mark all fields as touched to show validation errors
    if (this.ticketForms.length > 0) {
      this.ticketForms.controls.forEach(control => {
        if (!control.valid) {
          control.markAllAsTouched();
        }
      });
    }
  }
  
  isFieldInvalid(formGroupIndex: number, controlName: string): boolean {
    const control = this.getTicketFormGroup(formGroupIndex).get(controlName);
    return control.invalid && (control.dirty || control.touched);
  }
}
